import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import logger from "@/utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);

      if (!sessionIdMatch) {
        logger.error("AuthCallback: No session_id in URL hash", { hash });
        toast.error("Sign-in didn't return a valid session. Please try again.");
        navigate("/");
        return;
      }

      const sessionId = sessionIdMatch[1];
      const currentPath = window.location.pathname;

      try {
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true },
        );

        const user = response.data;
        logger.info("AuthCallback: session exchange OK", {
          email: user?.email,
          is_admin: user?.is_admin,
          path: currentPath,
        });

        // Clear the OAuth hash fragment so refreshes/back-navigation don't
        // re-trigger AuthCallback with a now-stale session_id.
        if (window.history && window.history.replaceState) {
          window.history.replaceState(
            null,
            "",
            window.location.pathname + window.location.search
          );
        }

        // Admins always go to the admin dashboard (their home), regardless
        // of which login entry they used. Clients always go to /portal.
        if (user.is_admin) {
          navigate("/admin", { state: { user }, replace: true });
        } else {
          navigate("/portal", { state: { user }, replace: true });
        }
      } catch (error) {
        const status = error.response?.status;
        const detail = error.response?.data?.detail;
        const detailMessage = typeof detail === "object" ? detail?.message : detail;
        const detailCode = typeof detail === "object" ? detail?.code : null;
        const detailEmail = typeof detail === "object" ? detail?.email : null;

        logger.error("AuthCallback: session exchange failed", {
          status,
          detail: detailMessage || detail,
          path: currentPath,
        });

        // Registration-required gate — send user to /contact with prefilled email
        if (status === 403 && detailCode === "registration_required") {
          toast.info(
            detailMessage ||
              "Please register on the Contact page before signing in.",
            { duration: 6000 }
          );
          navigate("/contact", {
            state: { prefillEmail: detailEmail, registrationRequired: true },
            replace: true,
          });
          return;
        }

        const userMsg =
          status === 401
            ? "Your sign-in session expired. Please try again."
            : status === 502
            ? "Authentication service temporarily unavailable. Please try again."
            : `Sign-in failed (${status || "network error"}). Please try again.`;
        toast.error(userMsg);
        navigate("/");
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="spinner w-8 h-8 mx-auto mb-4" />
        <p className="text-gray-600">Signing you in...</p>
      </div>
    </div>
  );
}
